/**
 * PDFImportPanel
 *
 * Component for importing bank statements from PDF files using AI extraction.
 * Shows PDF upload, extraction progress, and results preview.
 */

import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui';
import { reconciliationService } from '../../services/reconciliation.service';
import type { PDFExtractionResult, PDFExtractedTransaction } from '@budgira/shared';

// Props for the panel
interface PDFImportPanelProps {
  onImport: (lines: PDFExtractedTransaction[]) => Promise<void>;
  onCancel: () => void;
  sessionName?: string;
  accountName?: string;
}

type PDFImportStep = 'upload' | 'extracting' | 'preview';

const PDFImportPanel: React.FC<PDFImportPanelProps> = ({
  onImport,
  onCancel,
  sessionName,
  accountName,
}) => {
  const { t } = useTranslation();

  // State
  const [step, setStep] = useState<PDFImportStep>('upload');
  const [fileName, setFileName] = useState<string>('');
  const [bankHint, setBankHint] = useState<string>('');
  const [extractionResult, setExtractionResult] = useState<PDFExtractionResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Handle file selection and extraction
  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setError(t('reconciliation.pdf.invalidType', 'Veuillez sélectionner un fichier PDF'));
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError(t('reconciliation.pdf.fileTooLarge', 'Le fichier est trop volumineux (max 10 Mo)'));
      return;
    }

    setFileName(file.name);
    setError(null);
    setStep('extracting');
    setIsLoading(true);

    try {
      // Read file as base64
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          // Remove data URL prefix (data:application/pdf;base64,)
          const base64Data = result.split(',')[1] || result;
          resolve(base64Data);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // Extract transactions via API
      const result = await reconciliationService.extractPDF(base64, file.name, bankHint || undefined);

      setExtractionResult(result);

      if (result.success && result.transactions.length > 0) {
        setStep('preview');
      } else {
        setError(result.error || t('reconciliation.pdf.noTransactions', 'Aucune transaction trouvée dans ce PDF'));
        setStep('upload');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('reconciliation.pdf.extractionFailed', 'Échec de l\'extraction'));
      setStep('upload');
    } finally {
      setIsLoading(false);
    }
  }, [bankHint, t]);

  // Handle import
  const handleImport = useCallback(async () => {
    if (!extractionResult?.transactions.length) return;

    setIsLoading(true);
    setError(null);

    try {
      await onImport(extractionResult.transactions);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('reconciliation.pdf.importFailed', 'Échec de l\'import'));
    } finally {
      setIsLoading(false);
    }
  }, [extractionResult, onImport, t]);

  // Format currency
  const formatAmount = (amount: number): string => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  // Format date
  const formatDate = (dateStr: string): string => {
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR');
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={onCancel}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t('reconciliation.pdf.title', 'Importer un relevé PDF')}
                </h1>
                {/* AI badge */}
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  IA
                </span>
              </div>
              {(sessionName || accountName) && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {sessionName}{sessionName && accountName && ' • '}{accountName}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="w-full px-4 mt-4">
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Step: Upload */}
        {step === 'upload' && (
          <div className="max-w-lg space-y-6">
            {/* Description */}
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  {t('reconciliation.pdf.description', 'L\'IA analysera votre relevé bancaire PDF pour en extraire automatiquement les transactions. Compatible avec tous les formats de banques.')}
                </p>
              </div>
            </div>

            {/* Bank hint */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('reconciliation.pdf.bankHint', 'Nom de la banque (optionnel)')}
              </label>
              <input
                type="text"
                value={bankHint}
                onChange={(e) => setBankHint(e.target.value)}
                placeholder={t('reconciliation.pdf.bankHintPlaceholder', 'Ex: Société Générale, BNP, Crédit Agricole...')}
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {t('reconciliation.pdf.bankHintHelp', 'Indique le nom de votre banque pour améliorer la précision de l\'extraction')}
              </p>
            </div>

            {/* File input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('reconciliation.pdf.selectFile', 'Sélectionner un fichier PDF')}
              </label>
              <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center hover:border-primary-400 dark:hover:border-primary-500 transition-colors">
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileChange}
                  className="hidden"
                  id="pdf-file-input"
                />
                <label htmlFor="pdf-file-input" className="cursor-pointer">
                  <div className="flex flex-col items-center gap-2">
                    <svg className="w-12 h-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 11v4m0 0v4m0-4h4m-4 0H7" />
                    </svg>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {t('reconciliation.pdf.dropOrClick', 'Cliquez pour sélectionner un fichier PDF')}
                    </span>
                    <span className="text-xs text-gray-500">
                      {t('reconciliation.pdf.maxSize', 'Maximum 10 Mo')}
                    </span>
                  </div>
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Step: Extracting */}
        {step === 'extracting' && (
          <div className="max-w-lg mx-auto text-center py-12">
            <div className="flex flex-col items-center gap-4">
              <div className="relative">
                <div className="w-16 h-16 border-4 border-primary-200 rounded-full"></div>
                <div className="absolute top-0 left-0 w-16 h-16 border-4 border-primary-600 rounded-full border-t-transparent animate-spin"></div>
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                {t('reconciliation.pdf.extracting', 'Extraction en cours...')}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('reconciliation.pdf.extractingDescription', 'L\'IA analyse votre relevé bancaire. Cela peut prendre quelques secondes.')}
              </p>
              {fileName && (
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  {fileName}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Step: Preview */}
        {step === 'preview' && extractionResult && (
          <div className="space-y-6">
            {/* Metadata */}
            {extractionResult.metadata && Object.keys(extractionResult.metadata).some(k => extractionResult.metadata[k as keyof typeof extractionResult.metadata]) && (
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                  {t('reconciliation.pdf.detectedInfo', 'Informations détectées')}
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
                  {extractionResult.metadata.bankName && (
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Banque:</span>
                      <span className="ml-1 text-gray-900 dark:text-white">{extractionResult.metadata.bankName}</span>
                    </div>
                  )}
                  {extractionResult.metadata.accountNumber && (
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Compte:</span>
                      <span className="ml-1 text-gray-900 dark:text-white">{extractionResult.metadata.accountNumber}</span>
                    </div>
                  )}
                  {extractionResult.metadata.periodStart && extractionResult.metadata.periodEnd && (
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Période:</span>
                      <span className="ml-1 text-gray-900 dark:text-white">
                        {formatDate(extractionResult.metadata.periodStart)} - {formatDate(extractionResult.metadata.periodEnd)}
                      </span>
                    </div>
                  )}
                  {extractionResult.metadata.openingBalance !== undefined && (
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Solde initial:</span>
                      <span className="ml-1 text-gray-900 dark:text-white">{formatAmount(extractionResult.metadata.openingBalance)}</span>
                    </div>
                  )}
                  {extractionResult.metadata.closingBalance !== undefined && (
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Solde final:</span>
                      <span className="ml-1 text-gray-900 dark:text-white">{formatAmount(extractionResult.metadata.closingBalance)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Warnings */}
            {extractionResult.warnings && extractionResult.warnings.length > 0 && (
              <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-1">
                  {t('reconciliation.pdf.warnings', 'Avertissements')}
                </h4>
                <ul className="text-sm text-yellow-700 dark:text-yellow-300 list-disc list-inside">
                  {extractionResult.warnings.map((warning, i) => (
                    <li key={i}>{warning}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Transaction count */}
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                {t('reconciliation.pdf.transactionsFound', 'Transactions extraites')}
              </h3>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {extractionResult.transactionCount} {t('reconciliation.pdf.transactions', 'transactions')}
              </span>
            </div>

            {/* Transactions table */}
            <div className="overflow-x-auto border rounded-lg dark:border-gray-600 max-h-96 overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      {t('reconciliation.pdf.date', 'Date')}
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      {t('reconciliation.pdf.description', 'Description')}
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      {t('reconciliation.pdf.amount', 'Montant')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {extractionResult.transactions.map((tx, i) => (
                    <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap">
                        {formatDate(tx.date)}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">
                        {tx.description}
                      </td>
                      <td className={`px-3 py-2 text-sm font-medium text-right whitespace-nowrap ${
                        tx.amount >= 0 
                          ? 'text-green-600 dark:text-green-400' 
                          : 'text-red-600 dark:text-red-400'
                      }`}>
                        {formatAmount(tx.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Model info */}
            {extractionResult.model && (
              <p className="text-xs text-gray-400 dark:text-gray-500 text-center">
                {t('reconciliation.pdf.extractedWith', 'Extrait avec')} {extractionResult.model}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-4 py-3">
        <div className="flex justify-between">
          <Button variant="ghost" onClick={onCancel}>
            {t('common.cancel', 'Annuler')}
          </Button>

          {step === 'preview' && (
            <Button
              variant="primary"
              onClick={handleImport}
              disabled={isLoading || !extractionResult?.transactions.length}
            >
              {isLoading
                ? t('common.importing', 'Import en cours...')
                : t('reconciliation.pdf.import', 'Importer {{count}} transactions', {
                    count: extractionResult?.transactionCount || 0,
                  })}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PDFImportPanel;
